import React from 'react';
import { useTranslation } from 'react-i18next';
import SavedCard from 'Core/components/SavedCard';
import NewCard from 'Core/components/NewCard';
import { AlertError } from 'Core/components/Alert';
import usePaymentStore, { stripePromiseSelector } from 'Core/stores/payment';
import { Elements } from '@stripe/react-stripe-js';
import { Column, Row } from 'Core/components/Layout';
import { StyledFormElement } from 'Core/components/FormElements/formElements.styled';
import { getCard } from 'Core/components/CreditCardLogos';
import { DestructiveButton, OutlineButton } from 'Core/components/Button/Button';
import { findByPropertyValue, getFormPrefix } from 'Core/utils/utils';
import { getAppConfig } from 'Core/config/appConfig';

export default function CardSelector({
  defaultValues = {},
  cardCount = 1,
  index = 0,
  form,
  paymentMethods = [],
  onRemove,
  children,
  namePrefix = '',
  cardError,
  update,
}) {
  const { t } = useTranslation();
  const prefix = getFormPrefix(namePrefix, index);
  const selectedPaymentMethodId = form.watch(`${prefix}paymentMethodId`);
  const selectedPaymentMethod = findByPropertyValue(
    paymentMethods,
    'paymentMethodId',
    selectedPaymentMethodId
  );

  const setCardToNewCard = (e) => {
    if (form.getValues().cards) {
      const cardIndex = e.currentTarget.dataset.index;
      form.setValue(`cards[${cardIndex}]paymentMethodId`, '');
      const formVals = form.getValues();
      formVals.cards[cardIndex].paymentMethodId = '';
      update(cardIndex, formVals.cards[cardIndex]);
    } else {
      form.setValue(`paymentMethodId`, '');
    }
  };

  return (
    <Elements
      stripe={usePaymentStore(stripePromiseSelector)}
      options={{ locale: getAppConfig().language }}
    >
      {cardError && <AlertError className="mb-4">{cardError}</AlertError>}

      {/* Card Selection Header */}
      {(paymentMethods.length > 0 || cardCount !== 1) && (
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          {paymentMethods.length > 0 && (
            <div className="flex-1">
              <StyledFormElement>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('common.selectExistingCard')}
                </label>
                <select
                  defaultValue={defaultValues.paymentMethodId}
                  {...form.register(`${prefix}paymentMethodId`)}
                  data-testid={`card-select-${index}`}
                >
                  {paymentMethods.map(({ paymentMethodId, preferred, card }, i) => {
                    const { name } = getCard(card.brand);
                    return (
                      <option key={paymentMethodId + i} value={paymentMethodId}>
                        {name} •••• {card.last4}
                        {preferred ? ` (${t('common.preferred')})` : ''}
                      </option>
                    );
                  })}
                  <option value="">{t('common.newCard')}</option>
                </select>
              </StyledFormElement>
            </div>
          )}

          {cardCount !== 1 && (
            <div className="md:self-end md:pb-1">
              <DestructiveButton
                data-testid="card-remove-btn"
                onClick={onRemove}
                disabled={cardCount === 1}
              >
                {t('common.removeCard')}
              </DestructiveButton>
            </div>
          )}
        </div>
      )}

      {/* Saved Card Display */}
      {selectedPaymentMethodId && (
        <div className="mb-4">
          <Row className="flex flex-wrap justify-between w-full items-end !mx-0">
            <Column className="!flex-col-12 sm:!flex-col-12 !mb-2 px-0">
              <SavedCard
                index={index}
                form={form}
                paymentMethod={selectedPaymentMethod}
                paymentMethodId={selectedPaymentMethodId}
                namePrefix={namePrefix}
              />
            </Column>
            <Column className="!flex-col-12 sm:!flex-col-12 !mb-0 !px-0">{children}</Column>
          </Row>
        </div>
      )}

      {/* Modern OR Divider - only when there are saved cards to choose from */}
      {paymentMethods.length > 0 && (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-sm font-medium text-gray-500">OR</span>
          </div>
        </div>
      )}

      {/* New Card Entry or Button */}
      {selectedPaymentMethodId ? (
        <div className="flex justify-center">
          <OutlineButton
            onClick={setCardToNewCard}
            data-testid="card-select-new"
            data-index={index}
            className="!px-6"
          >
            {t('common.enterNewCardDetails')}
          </OutlineButton>
        </div>
      ) : (
        <div className="space-y-4">
          <NewCard
            defaultValues={defaultValues}
            index={index}
            form={form}
            namePrefix={namePrefix}
          />
          {children}
        </div>
      )}
    </Elements>
  );
}
